/**
 * Target Language Translations
 *
 * This file contains UI elements that should appear in the target language
 * (the language the user is learning), not their native language.
 *
 * Examples:
 * - For English learning (?learn=en): "Start" instead of "Commencer"
 * - For French learning (default): "Commencer" instead of "Start"
 * - For Spanish learning (?learn=es): "Empezar" instead of "Commencer"
 */

export const targetLanguageTranslations = {
  // French (default)
  fr: {
    startButton: "Commencer",
    continueYourLearningPlan: "Continuez votre plan",
    startYourLearningPlan: "Commencez votre plan",
    greeting: "Bonjour!",
    initialInstruction: "Pour commencer, clique sur le micro et dis «Bonjour!».",
    activityInstruction: "Nous allons pratiquer l'activité {activity}. Dis \"oui\" pour commencer.",
    defaultInstruction: "Clique le bouton vert et dis moi \"Bonjour!\".",
    yes: "oui",
    didntCatch: "Désolée, je n'ai pas bien entendu. Peux-tu répéter, s'il te plaît ?"
  },

  // English
  en: {
    startButton: "Start",
    continueYourLearningPlan: "Continue your plan",
    startYourLearningPlan: "Start your plan",
    greeting: "Hello!",
    initialInstruction: "To get started, click the microphone and say \"Hello!\".",
    activityInstruction: "We're going to practice the activity {activity}. Say \"yes\" to start.",
    defaultInstruction: "Click the green button and tell me \"Hello!\".",
    yes: "yes",
    didntCatch: "Sorry, I didn't catch that. Could you say it again, please?"
  },

  // Spanish
  es: {
    startButton: "Empezar",
    continueYourLearningPlan: "Continúa tu plan",
    startYourLearningPlan: "Empieza tu plan",
    greeting: "¡Hola!",
    initialInstruction: "Para empezar, haz clic en el micrófono y di \"¡Hola!\".",
    activityInstruction: "Vamos a practicar la actividad {activity}. Di \"sí\" para empezar.",
    defaultInstruction: "Haz clic en el botón verde y dime \"¡Hola!\".",
    yes: "sí",
    didntCatch: "Perdona, no te he entendido bien. ¿Puedes repetirlo, por favor?"
  },

  // German
  de: {
    startButton: "Anfangen",
    continueYourLearningPlan: "Setzen Sie Ihren Plan fort",
    startYourLearningPlan: "Starten Sie Ihren Plan",
    greeting: "Hallo!",
    initialInstruction: "Um zu beginnen, klicken Sie auf das Mikrofon und sagen Sie \"Hallo!\".",
    activityInstruction: "Wir werden die Aktivität {activity} üben. Sagen Sie \"ja\" zum Starten.",
    defaultInstruction: "Klicken Sie auf den grünen Button und sagen Sie mir \"Hallo!\".",
    yes: "ja",
    didntCatch: "Entschuldigung, das habe ich nicht verstanden. Kannst du es bitte wiederholen?"
  },

  // Italian
  it: {
    startButton: "Iniziare",
    continueYourLearningPlan: "Continua il tuo piano",
    startYourLearningPlan: "Inizia il tuo piano",
    greeting: "Ciao!",
    initialInstruction: "Per iniziare, clicca sul microfono e di' \"Ciao!\".",
    activityInstruction: "Praticheremo l'attività {activity}. Di' \"sì\" per iniziare.",
    defaultInstruction: "Clicca il pulsante verde e dimmi \"Ciao!\".",
    yes: "sì",
    didntCatch: "Scusa, non ho capito bene. Puoi ripeterlo, per favore?"
  },

  // Swedish (beta)
  sv: {
    startButton: "Starta",
    continueYourLearningPlan: "Fortsätt din plan",
    startYourLearningPlan: "Starta din plan",
    greeting: "Hej!",
    initialInstruction: "För att komma igång, klicka på mikrofonen och säg \"Hej!\".",
    activityInstruction: "Vi ska öva på aktiviteten {activity}. Säg \"ja\" för att börja.",
    defaultInstruction: "Klicka på den gröna knappen och säg \"Hej!\" till mig.",
    yes: "ja",
    didntCatch: "Förlåt, jag hörde inte riktigt. Kan du säga det igen, tack?"
  },
  zh: {
    startButton: "开始",
    continueYourLearningPlan: "继续你的学习计划",
    startYourLearningPlan: "开始你的学习计划",
    greeting: "你好！",
    initialInstruction: "开始之前，请点击麦克风并说“你好！”。",
    activityInstruction: "我们来练习{activity}。说“是”就可以开始。",
    defaultInstruction: "点击绿色按钮，对我说“你好！”。",
    yes: "是",
    didntCatch: "抱歉，我没有听清楚。可以请你再说一遍吗？"
  }
};

/**
 * Get target language translation
 * @param {string} targetLanguageCode - Language code (e.g., 'en', 'fr', 'es')
 * @param {string} key - Translation key (e.g., 'startButton', 'greeting')
 * @param {object} replacements - Object with replacement values for templated strings
 * @returns {string} Translated text in target language
 */
export const getTargetLanguageText = (targetLanguageCode, key, replacements = {}) => {
  const translations = targetLanguageTranslations[targetLanguageCode] || targetLanguageTranslations.fr;
  let text = translations[key] || targetLanguageTranslations.fr[key] || '';

  // Replace template variables like {activity}
  Object.keys(replacements).forEach(replaceKey => {
    text = text.replace(`{${replaceKey}}`, replacements[replaceKey]);
  });

  return text;
};